import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { APP_NAME, APP_VERSION, MODULES, PLAN_LABELS, type Industry, type Plan } from '@kontor/shared';
import { useSession } from '../session';
import { useRowActionIcons } from './rowActionIcons';
import { PlanIcon } from './PlanIcon';
import { GroupIcon, HomeIcon, NavIcon } from './NavIcon';
import { GlobalSearch } from './GlobalSearch';
import { ThemeToggle } from './ThemeToggle';
import { HelpLink, NotificationBell, UserMenu } from './TopbarMenus';
import { PageHelp } from './PageHelp';
import { HELP_HOME } from '../links';
import { TrialNotice } from './TrialNotice';
import { SETUP_MODULES, useSetupSections } from '../setupSections';
import { buildNavGroups } from '../navBuilder';

/**
 * Aufgeklappte Gruppen der Seitenleiste merkt sich der Browser. Ohne Eintrag sind alle Gruppen eingeklappt (Änderungswunsch vom 21.10.2026), nur die Gruppe der Seite, auf der man gerade
 * ist, klappt von selbst auf. Der frühere Schlüssel „kontor.nav.collapsed“ galt umgekehrt (gemerkt wurde, was zu war) und wird nicht mehr gelesen.
 */
const OPEN_KEY = 'kontor.nav.open';
function readOpen(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
/** Schmale Seitenleiste am Desktop (nur Symbole). Unter 900 px gibt es sie nicht, dort bleibt die Schublade. */
const RAIL_KEY = 'kontor.nav.rail';
function readRail(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}
const onPath = (pathname: string, path: string | null) => !!path && (pathname === path || pathname.startsWith(`${path}/`));

/** Kurzname der Branche im Kopf: „Wirkia-Chauffeur“ */
const INDUSTRY_SHORT: Record<Industry, string> = { chauffeur: 'Chauffeur', gastronomy: 'Gastronomie', wine: 'Weinhandel' };

export function AppShell() {
  const { session, can } = useSession();
  useRowActionIcons();
  const setupSections = useSetupSections();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [opened, setOpened] = useState<Set<string>>(readOpen);
  const [rail, setRail] = useState(readRail);
  // Hauptbereich, dessen Einträge die schmale Leiste gerade zeigt (null = die Hauptbereiche selbst)
  const [railGroupKey, setRailGroupKey] = useState<string | null>(null);
  const toggleRail = () =>
    setRail((current) => {
      try {
        localStorage.setItem(RAIL_KEY, current ? '0' : '1');
      } catch {
        // ohne Speicher gilt die Breite nur bis zum Neuladen
      }
      return !current;
    });
  // Gruppen und Einträge, wie sie zuletzt gezeichnet wurden: Nach einem Seitenwechsel klappt die Gruppe der neuen Seite von selbst auf
  const groupsRef = useRef<{ key: string; items: { path: string | null }[] }[]>([]);

  const toggleGroup = (key: string) =>
    setOpened((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
      } catch {
        // ohne Speicher gilt das Aufklappen nur bis zum Neuladen
      }
      return next;
    });
  useEffect(() => {
    const group = groupsRef.current.find((g) => g.items.some((i) => onPath(location.pathname, i.path)));
    if (group) setOpened((current) => (current.has(group.key) ? current : new Set([...current, group.key])));
  }, [location.pathname]);

  // Ein Seitenwechsel beginnt oben: Wer vorher weit unten war, soll die neue Seite von Anfang an sehen. Nur der Pfad zählt, Filter und Blättern in der Adresszeile (?page=2)
  // lassen die Seite stehen. Springt die Adresse auf einen Anker (#), bleibt es dem Browser überlassen.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash]);

  // Das Untermenü der schmalen Leiste schließt nach einem Seitenwechsel, mit Escape und bei einem Klick daneben
  useEffect(() => {
    setRailGroupKey(null);
  }, [location.pathname, rail]);
  useEffect(() => {
    if (!railGroupKey) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setRailGroupKey(null);
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.sidenav__rail')) setRailGroupKey(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [railGroupKey]);

  // Auf kleinen Bildschirmen ist das Menü eine Schublade. Sie schließt nach jedem Seitenwechsel und mit Escape.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  if (!session) return null;

  // Benutzer und API stehen nicht in der Seitenleiste, die Einrichtung steht in der Gruppe System
  // Ein Modul steht in der Seitenleiste, wenn es lizenziert ist und die Rolle es lesen darf. Module in Vorbereitung sieht nur der Inhaber als Hinweis,
  // alle anderen sehen nur, was sie ansehen oder verwalten dürfen.
  const isOwner = session.user.role.key === 'owner';
  // „API und Webhooks“ (Premium) steht nur in der Einrichtung (Bereich „APIs und Webhooks“), nicht in der Seitenleiste.
  const licensed = MODULES.filter((m) => session.tenant.modules.includes(m.key) && !SETUP_MODULES.has(m.key) && m.key !== 'api_access' && (m.available ? can(`${m.key}.read`) : isOwner));
  // Die Seitenleiste: lizenzierte Module in Gruppen (siehe navBuilder.ts). Bereiche, die das Unternehmen in Einrichtung → Branche ausgeblendet hat, stehen nicht darin.
  // Die Seiten selbst bleiben über ihre Adresse erreichbar, die Einrichtung lässt sich nie ausblenden.
  const hiddenAreas = new Set(session.tenant.hiddenAreas ?? []);
  const groups = buildNavGroups({ licensed, can, withSetup: setupSections.length > 0 })
    .map((g) => ({ ...g, label: session.tenant.groupLabels?.[g.key] || g.label, items: g.items.filter((i) => i.key === '/einrichtung' || !hiddenAreas.has(i.key)) }))
    .filter((g) => g.items.length > 0);
  groupsRef.current = groups;
  const railGroup = groups.find((g) => g.key === railGroupKey) ?? null;
  // Schnellaktionen der Suche („Neues Angebot“), nur was das Modul und das Schreibrecht der Rolle erlauben
  const creates: { label: string; path: string; module: string; permission: string; keywords: string[] }[] = [
    { label: 'Neue Anfrage', path: '/anfragen/neu', module: 'inquiries', permission: 'inquiries.write', keywords: ['anlegen', 'erfassen'] },
    { label: 'Neues Angebot', path: '/angebote/neu', module: 'offers', permission: 'offers.write', keywords: ['anlegen', 'schreiben', 'offerte'] },
    { label: 'Neuer Auftrag', path: '/auftraege/neu', module: 'orders', permission: 'orders.write', keywords: ['anlegen', 'bestellung'] },
    { label: 'Neue Rechnung', path: '/rechnungen/neu', module: 'invoices_out', permission: 'invoices_out.write', keywords: ['anlegen', 'schreiben', 'ausgangsrechnung'] },
    { label: 'Neue Eingangsrechnung', path: '/eingangsrechnungen/neu', module: 'invoices_in', permission: 'invoices_in.write', keywords: ['erfassen', 'hochladen', 'lieferant'] },
    { label: 'Neuer Kontakt', path: '/kontakte/neu', module: 'contacts', permission: 'contacts.write', keywords: ['anlegen', 'kunde', 'person', 'firma'] },
    { label: 'Neues Produkt', path: '/produkte/neu', module: 'products', permission: 'products.write', keywords: ['anlegen', 'artikel', 'ware'] },
    { label: 'Neue Aufgabe', path: '/kalender/aufgaben/neu', module: 'calendar', permission: 'calendar.write', keywords: ['anlegen', 'todo'] },
    { label: 'Neuer Termin', path: '/kalender/termine/neu', module: 'calendar', permission: 'calendar.write', keywords: ['anlegen', 'kalender'] },
    { label: 'Neuer Mitarbeiter', path: '/mitarbeiter/neu', module: 'hr', permission: 'hr.write', keywords: ['anlegen', 'personal'] },
    { label: 'Fahrtenbuch eines Fahrzeugs', path: '/flotte', module: 'fleet', permission: 'fleet.write', keywords: ['fahrtenbuch', 'fahrt', 'eintragen', 'auto'] },
  ];
  const searchActions = creates.filter((c) => session.tenant.modules.includes(c.module) && can(c.permission)).map(({ label, path, keywords }) => ({ label, path, keywords }));

  return (
    <div className={`shell${rail ? ' shell--rail' : ''}`}>
      <header className="topbar">
        <div className="topbar__left">
          <button type="button" className="topbar__menu" aria-label="Menü öffnen" aria-expanded={menuOpen} aria-controls="mainnav" onClick={() => setMenuOpen((v) => !v)}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        <NavLink to="/" className="brand">
          <img className="brand__mark" src="/brand/wirkia-symbol.webp" alt="" width="60" height="26" />
          <span>
            <span className="brand__name">
              Wirkia
              {session.tenant.industry && <span className="brand__industry">-{INDUSTRY_SHORT[session.tenant.industry as Industry] ?? ''}</span>}
            </span>
            <small>{session.tenant.displayName}</small>
          </span>
        </NavLink>
        </div>
        <GlobalSearch
          pages={groups.flatMap((g) => g.items.filter((i): i is typeof i & { path: string } => !!i.path).map((i) => ({ label: i.label, path: i.path, group: g.label })))}
          sections={setupSections}
          actions={searchActions}
        />
        <div className="topbar__user">
          <HelpLink />
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      {menuOpen && <div className="nav-backdrop" onClick={() => setMenuOpen(false)} />}
      <nav id="mainnav" className={`sidenav${menuOpen ? ' sidenav--open' : ''}`} aria-label="Module">
        <div className="sidenav__head">
          <strong>Menü</strong>
          <button type="button" className="sidenav__close" onClick={() => setMenuOpen(false)} aria-label="Menü schließen">×</button>
        </div>
        <button
          type="button"
          className="sidenav__rail-toggle"
          onClick={toggleRail}
          aria-label={rail ? 'Menü ausklappen' : 'Menü einklappen'}
          aria-pressed={rail}
          title={rail ? 'Menü ausklappen' : 'Menü einklappen'}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 6-6 6 6 6" />
          </svg>
        </button>
        {/* Eingeklappt (nur Desktop): erst die Hauptbereiche als Symbole, ein Klick zeigt deren Einträge, der Pfeil führt zurück */}
        <div className="sidenav__rail">
          <NavLink to="/" end className="sidenav__railbtn" title="Übersicht" aria-label="Übersicht">
            <HomeIcon />
          </NavLink>
          <div className="sidenav__raillist" role="group" aria-label="Hauptbereiche">
            {groups.map((g) => (
              <button
                key={g.key}
                type="button"
                className={`sidenav__railbtn${g.items.some((i) => onPath(location.pathname, i.path)) ? ' is-current' : ''}${railGroup?.key === g.key ? ' is-open' : ''}`}
                title={g.label}
                aria-label={g.label}
                aria-expanded={railGroup?.key === g.key}
                aria-controls="railflyout"
                onClick={() => setRailGroupKey((cur) => (cur === g.key ? null : g.key))}
              >
                <GroupIcon groupKey={g.key} />
              </button>
            ))}
          </div>
          {/* Untermenü als Erweiterung der schmalen Leiste: Symbol und Text wie in der ausgeklappten Navigation */}
          {railGroup && (
            <div id="railflyout" className="sidenav__flyout" role="group" aria-label={railGroup.label}>
              <strong className="sidenav__flyout-title">{railGroup.label}</strong>
              {railGroup.items.map((item) =>
                item.path ? (
                  <NavLink key={item.key} to={item.path}>
                    <NavIcon itemKey={item.key} />
                    <span className="sidenav__label">{item.label}</span>
                  </NavLink>
                ) : (
                  <span key={item.key} className="sidenav__soon" title="In Vorbereitung">
                    <NavIcon itemKey={item.key} />
                    <span className="sidenav__label">{item.label}</span>
                  </span>
                ),
              )}
            </div>
          )}
        </div>
        <div className="sidenav__full">
        <div className="sidenav__row">
          <NavLink to="/" end className="sidenav__home" title="Übersicht">
            <HomeIcon />
            <span className="sidenav__label">Übersicht</span>
          </NavLink>
          {location.pathname === '/' && <PageHelp />}
        </div>
        {groups.map((g) => {
          const open = opened.has(g.key);
          return (
            <div key={g.key} className={`sidenav__group${open ? '' : ' is-collapsed'}`} role="group" aria-labelledby={`navgroup-${g.key}`}>
              <button type="button" className="sidenav__title" id={`navgroup-${g.key}`} aria-expanded={open} aria-controls={`navitems-${g.key}`} onClick={() => toggleGroup(g.key)}>
                <span className="sidenav__label">{g.label}</span>
                <svg className="sidenav__chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <div id={`navitems-${g.key}`} className="sidenav__items" hidden={!open}>
                {g.items.map((item) =>
                  item.path ? (
                    <div key={item.key} className="sidenav__row">
                      <NavLink to={item.path} title={item.label}>
                        <NavIcon itemKey={item.key} />
                        <span className="sidenav__label">{item.label}</span>
                      </NavLink>
                      {/* Die Kurzhilfe zur Seite, auf der man gerade ist, steht am Eintrag im Menü */}
                      {onPath(location.pathname, item.path) && <PageHelp />}
                    </div>
                  ) : (
                    <span key={item.key} className="sidenav__soon" title={`${item.label} (in Vorbereitung)`}>
                      <NavIcon itemKey={item.key} />
                      <span className="sidenav__label">{item.label}</span>
                    </span>
                  ),
                )}
              </div>
            </div>
          );
        })}
        </div>
        <p className="sidenav__plan">
          <PlanIcon plan={session.tenant.plan} size={18} />
          <span>Paket <strong>{PLAN_LABELS[session.tenant.plan as Plan] ?? session.tenant.plan}</strong></span>
        </p>
        <a className="sidenav__help" href={HELP_HOME} target="_blank" rel="noopener noreferrer">Anleitung und Hilfe<span className="sr-only"> (öffnet in neuem Tab)</span></a>
        {/* Wer die Einrichtung sehen darf, öffnet mit der Version das Wirkia Konto. Für alle anderen steht sie nur da. */}
        {setupSections.some((s) => s.path === 'ueber') ? (
          <NavLink to="/einrichtung/wirkia-konto" className="sidenav__version" title="Wirkia Konto">{APP_NAME} Version {APP_VERSION}</NavLink>
        ) : (
          <span className="sidenav__version">{APP_NAME} Version {APP_VERSION}</span>
        )}
        {/* Auf dem Handy steht der Umschalter für das Design ganz unten im Menü, am Desktop in der Kopfleiste */}
        <div className="sidenav__mobile-only sidenav__theme">
          <ThemeToggle variant="row" />
        </div>
      </nav>

      <main className="content" id="inhalt">
        <TrialNotice />
        <Outlet />
      </main>
    </div>
  );
}
